import { Link, Outlet, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { AlertTriangle, Compass } from 'lucide-react'

import { useVocabulary } from '@/api/vocab'
import { AiHealthBanner } from '@/components/AiHealthBanner'
import { AppShell } from '@/components/AppShell'
import { CommandPalette } from '@/components/CommandPalette'
import { QueueStrip } from '@/components/QueueStrip'
import { Button } from '@/components/ui/button'
import { isStaleChunkError, reloadForStaleChunk, staleReloadPending } from '@/lib/staleChunk'

/**
 * App shell for every route: top nav, AI health banner, the page, the
 * queue strip, and the command palette. The toaster and the SSE bridge are
 * mounted once in `main.tsx`, outside the router, so they survive
 * navigation and error boundaries.
 *
 * The vocabulary is loaded here, once, for the whole app (§9.1): the annotator, the
 * library filters and the clip-name reader all read `vocabStore`, and without this only
 * `/settings/vocabulary` ever filled it — every other screen silently fell back to the
 * annotator's built-in table of eight kinds and never saw an operator's `x:` kind.
 */
export function RootLayout() {
  useVocabulary()
  return (
    <AppShell banner={<AiHealthBanner />} strip={<QueueStrip />}>
      <Outlet />
      <CommandPalette />
    </AppShell>
  )
}

export function NotFound() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-24 text-center">
      <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-paper-2 text-ink-2">
        <Compass className="size-7" aria-hidden />
      </span>
      <h1 className="text-2xl font-semibold text-ink">Nothing here</h1>
      <p className="text-sm text-ink-2">That page does not exist. Your puppets and stories are one tap away.</p>
      <div className="flex gap-2">
        <Button asChild variant="primary">
          <Link to="/">Home</Link>
        </Button>
        <Button asChild>
          <Link to="/puppets">Puppets</Link>
        </Button>
      </div>
    </div>
  )
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const message = error instanceof Error ? error.message : String(error)
  // Old code asking for a chunk a redeploy removed: reload once for the new build.
  if ((isStaleChunkError(error) && reloadForStaleChunk()) || staleReloadPending()) {
    return (
      <p className="grid min-h-[40vh] place-items-center text-sm text-ink-2" role="status">
        Updating the studio…
      </p>
    )
  }
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-24 text-center" role="alert">
      <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-danger-soft text-danger">
        <AlertTriangle className="size-7" aria-hidden />
      </span>
      <h1 className="text-2xl font-semibold text-ink">Something went wrong</h1>
      <p className="max-w-sm break-words text-sm text-ink-2">{message}</p>
      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          Try again
        </Button>
        <Button asChild>
          <Link to="/">Home</Link>
        </Button>
      </div>
    </div>
  )
}
